/**
 * @file useCheckAuthAndFetch.js
 * @module src/js/hooks/useCheckAuthAndFetch
 * @description
 * **A hook to check authentication and fetch data from the backend API for protected pages.**
 * - Returns a function that:
 *   - Redirects to the login page if the user is not authenticated.
 *   - Fetches data from specified backend endpoints with authentication.
 *   - Utilizes the `useFetchFromBackend` hook for API calls.
 *
 * @version
 * <p>
 *  2025-12-05: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useNavigate, useLocation } from "react-router-dom";
import { useCallback, useRef } from "react";
import { useUser } from "../contexts/UserProvider";
import useFetchFromBackend from "./useFetchFromBackend";

export default function useCheckAuthAndFetch() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { uid } = useUser();
    const fetchFromBackend = useFetchFromBackend();
    const uidRef = useRef(uid);

    const checkAuthAndFetch = useCallback(async ({
        request,
        fetchData = true,
    }) => {
        if (fetchData && request == null) throw new Error("Request is required");

        // If we use uid directly, when token expires and uid becomes 'Guest', it triggers the following `navigate` call.
        // `state` will be set as notLoggedIn, causing the notLoggedIn message to show up on the login page.
        // But the real cause of redirection is token expiry, not that the user is not logged in.
        // On the other hand, `fetchFromBackend` will be called again due to uid changing,
        // leading to redundant logout requests.
        // Therefore, we use a ref to store the initial uid value and use it here.
        // Ref in nature will not trigger re-renders, so it's safe to exclude it from the dependency array.
        if (uidRef.current === 'Guest') {
            navigate('/login', { replace: true, state: { from: pathname, notLoggedIn: true } });
            return;
        }
        
        if (fetchData) {
            const data = await fetchFromBackend({
                request,
                requireAuth: true,
                from: pathname,
            });
            return data;
        }

    }, [fetchFromBackend, navigate, pathname]);

    return checkAuthAndFetch;
}